import { useRoute } from '@react-navigation/native';
import {
  Button,
  Headline3,
  HorizontalRadioList,
  HorizontalRadioListData,
  Spinner,
} from 'aimo-ui';
import SectionListCustom from 'components/history/SectionListCustom';
import { format, isValid } from 'date-fns';
import useParkingHistoryFilter, {
  HistoryTypeFilter,
} from 'hooks/useParkingHistoryFilter';
import { concat, groupBy, isFunction } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistoryStore } from 'stores/historyStore';
import styled from 'styled-components/native';
import {
  ColumnWithMargins,
  SheetDetailsWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  ChargingSession,
  ParkingSession,
  Receipt,
} from 'types/generatedSchemaTypes';
import { getLocale } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import {
  getReceiptHistoryData,
  getSessionData,
  HistoryScreenName,
} from 'utils/historyUtils';
import { sortReceiptsByStartTime } from 'utils/parkUtils';
import FilteredSessionContent from './FilteredSessionContent';
import { NoHistory } from './HistoryComponents';
import HistoryItem from './HistoryItem';

const TitleElement = styled(Headline3)`
  margin-bottom: 20px;
  padding-bottom: 8px;
  background-color: ${(p) => p.theme.colors.white};
`;

const Separator = styled.View`
  border-bottom-width: 3px;
  margin-top: -3px;
  border-bottom-color: ${(p) => p.theme.colors.gray50};
`;

const WholePageSpinnerWrapper = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

export type ReceiptHistoryData = {
  title: string;
  data: Receipt[];
  groupIndex: number;
}[];

export type SessionHistoryData = {
  title: string;
  data: ParkingSession[];
  groupIndex: number;
}[];

type RouteParams = {
  filter?: HistoryTypeFilter;
};

type HistoryContentProps = {
  receiptData?: Receipt[];
  failedSessions?: (ParkingSession | ChargingSession)[];
  prepaidSessions?: ParkingSession[];
  sessionsFetchMore?: () => void;
  fetchMore?: () => void;
  loading: boolean;
  sessionsLoading?: boolean;
  sessionsIsLast?: boolean;
  isLast?: boolean;
  historyScreenName?: HistoryScreenName;
};

type ListFooterProps = {
  isLast: boolean;
  fetchMore?: () => void;
  loading: boolean;
};

const WholePageSpinner = () => (
  <WholePageSpinnerWrapper testID="whole-page-spinner-wrapper">
    <Spinner />
  </WholePageSpinnerWrapper>
);

export const ListFooter = ({ isLast, fetchMore, loading }: ListFooterProps) => {
  const { t } = useTranslation();

  if (isLast || !isFunction(fetchMore)) {
    return <></>;
  }

  return (
    <StyledWrapper mb={10} center maxWidth={200}>
      <Button
        disabled={loading}
        onPress={fetchMore}
        text={t('button.showMore')}
        testID="show-more"
        loading={loading}
      />
    </StyledWrapper>
  );
};

const renderSectionHeader = ({
  section: { title, groupIndex },
}: {
  section: ReceiptHistoryData[number];
}) => <TitleElement testID={`parking-${groupIndex}`}>{title}</TitleElement>;

const renderHistoryItem = ({
  item,
  index,
  section,
}: {
  item: ReceiptHistoryData;
  index: number;
  section: ReceiptHistoryData[number];
}) => <HistoryItem section={section} item={item} index={index} />;

function formatDateAsMonthYear(dateToFormat: Date | null | undefined) {
  if (!dateToFormat) {
    return '';
  }

  const date = new Date(dateToFormat);
  if (!isValid(date)) {
    return '';
  }

  return format(date, 'LLLL yyyy', {
    locale: getLocale(),
  });
}

function sortSessionsByStartTime(sessionsToSort: any[]) {
  return concat<any>(sessionsToSort).sort((a, b) => {
    const dateA = new Date(a.startDate || a.startTime || a.created).getTime();
    const dateB = new Date(b.startDate || b.startTime || b.created).getTime();
    return dateB > dateA ? 1 : -1;
  }) as ParkingSession[] & ChargingSession[];
}

const getStartTimeOfSession = (session: ParkingSession & ChargingSession) => {
  if (session.startTime) {
    return session.startTime;
  }
  return session?.startDate ?? session?.created;
};

const HistoryContent: React.FC<HistoryContentProps> = ({
  receiptData = [],
  failedSessions = [],
  prepaidSessions = [],
  sessionsFetchMore,
  fetchMore,
  loading,
  sessionsLoading,
  sessionsIsLast = true,
  isLast = true,
  historyScreenName = 'PARKING_AND_CHARGING',
}) => {
  const {
    historyViewTypeFilters: parkingAndChargingHistoryViewTypeFilters,
    prepaidPermitHistoryViewTypeFilters,
  } = useParkingHistoryFilter();
  const [isInitialLoad, setIsInitialLoad] = useState(loading);

  const selectedHistoryTypeFilter =
    useHistoryStore((state) =>
      historyScreenName === 'PARKING_AND_CHARGING'
        ? state.parkingHistoryViewFilter
        : state.prepaidPermitHistoryViewFilter
    ) || 'receipts';

  const handleOnSelectedParkingType = useCallback(
    (newValue: HorizontalRadioListData<HistoryTypeFilter>) => {
      if (historyScreenName === 'PARKING_AND_CHARGING') {
        useHistoryStore.getState().setParkingHistoryViewFilter(newValue.value);
      }

      if (historyScreenName === 'PREPAID_AND_PERMIT') {
        useHistoryStore
          .getState()
          .setPrepaidPermitHistoryViewFilter(newValue.value);
      }
    },
    [historyScreenName]
  );

  const getHistoryViewTypeFilters = (screen: HistoryScreenName) => {
    if (screen === 'PARKING_AND_CHARGING') {
      return parkingAndChargingHistoryViewTypeFilters;
    }

    if (screen === 'PREPAID_AND_PERMIT') {
      return prepaidPermitHistoryViewTypeFilters;
    }

    return parkingAndChargingHistoryViewTypeFilters;
  };

  const route = useRoute();
  useEffect(() => {
    const routeParams: RouteParams = route?.params ?? {};
    const routeParamsFilter = routeParams?.filter;

    if (isInitialLoad && !loading) {
      if (routeParamsFilter) {
        const historyViewTypeFilter =
          parkingAndChargingHistoryViewTypeFilters.find(
            (filter) => filter.value === routeParamsFilter
          );
        if (historyViewTypeFilter) {
          handleOnSelectedParkingType(historyViewTypeFilter);
        }
      }
      setIsInitialLoad(false);
    }
  }, [
    loading,
    isInitialLoad,
    historyScreenName,
    route?.params,
    parkingAndChargingHistoryViewTypeFilters,
    handleOnSelectedParkingType,
  ]);

  const receiptsGroupedByMonth = useMemo(
    () =>
      groupBy(sortReceiptsByStartTime(receiptData), (receipt) => {
        const startDate = receipt?.items?.[0]?.startTime || receipt?.created;
        return formatDateAsMonthYear(startDate);
      }),
    [receiptData]
  );

  const sessionsToSort =
    historyScreenName === 'PARKING_AND_CHARGING'
      ? failedSessions
      : prepaidSessions;
  const sessionsByStartTime = sortSessionsByStartTime(sessionsToSort);
  const sessionsGroupedByMonth = groupBy(
    sessionsByStartTime,
    (sessionToListInHistory) => {
      const startDate = getStartTimeOfSession(sessionToListInHistory);
      return formatDateAsMonthYear(startDate);
    }
  );

  const receiptHistoryData = getReceiptHistoryData(receiptsGroupedByMonth);
  const sessionData = getSessionData(sessionsGroupedByMonth);

  const isReceipts = selectedHistoryTypeFilter === 'receipts';
  const isFailedForParkingCharging =
    selectedHistoryTypeFilter === 'failed' &&
    historyScreenName === 'PARKING_AND_CHARGING';
  const isFreeForParkingCharging =
    selectedHistoryTypeFilter === 'free' &&
    historyScreenName === 'PARKING_AND_CHARGING';
  const isBillingForParkingCharging =
    selectedHistoryTypeFilter === 'billing' &&
    historyScreenName === 'PARKING_AND_CHARGING';
  const isOtherForParkingCharging =
    selectedHistoryTypeFilter === 'other' &&
    historyScreenName === 'PARKING_AND_CHARGING';

  const isBillingForPrepaidPermit =
    selectedHistoryTypeFilter === 'billing' &&
    historyScreenName === 'PREPAID_AND_PERMIT';

  return (
    <SheetDetailsWrapper pt={isWeb ? 50 : 30}>
      {isInitialLoad ? (
        <WholePageSpinner />
      ) : (
        <>
          {historyScreenName !== 'SUBSCRIPTION' && (
            <ColumnWithMargins mb={20} mt={20}>
              <HorizontalRadioList
                selectedValue={selectedHistoryTypeFilter}
                listData={getHistoryViewTypeFilters(historyScreenName)}
                onSelected={handleOnSelectedParkingType}
              />
            </ColumnWithMargins>
          )}
          {(isFailedForParkingCharging ||
            isFreeForParkingCharging ||
            isBillingForParkingCharging ||
            isOtherForParkingCharging) && (
            <FilteredSessionContent
              sessions={sessionData}
              sessionsFetchMore={sessionsFetchMore}
              isLast={sessionsIsLast}
              loading={sessionsLoading}
              noData={failedSessions.length === 0}
            />
          )}
          {isBillingForPrepaidPermit && (
            <FilteredSessionContent
              historyScreenName={'PREPAID_AND_PERMIT'}
              sessions={sessionData}
              sessionsFetchMore={sessionsFetchMore}
              isLast={sessionsIsLast}
              loading={sessionsLoading}
              noData={prepaidSessions.length === 0}
            />
          )}
          {receiptHistoryData.length > 0 && isReceipts && (
            <SectionListCustom
              refreshing={loading}
              sections={receiptHistoryData}
              stickySectionHeadersEnabled={true}
              keyExtractor={({ sessionId = '' }, index: number) =>
                sessionId + index
              }
              renderItem={renderHistoryItem}
              renderSectionHeader={renderSectionHeader}
              ItemSeparatorComponent={Separator}
              ListFooterComponent={
                <ListFooter
                  isLast={isLast}
                  fetchMore={fetchMore}
                  loading={loading}
                />
              }
            />
          )}
          {receiptHistoryData.length === 0 && !loading && isReceipts && (
            <NoHistory />
          )}
        </>
      )}
    </SheetDetailsWrapper>
  );
};

export default HistoryContent;
